// src/modules/games/components/PracticeGame/GameResultDuel.js

import React, { useState, useEffect, useRef } from "react"; 
import Feedback from "./Feedback";
import {
  Button,
  GreenButton,
  RedButton
} from "../../../globals/components/button";
import styled from "styled-components";
import { Scale } from 'react-scaling';

import useAuth from "../../auth/hooks/useAuth";
import axios from "axios";
import { ToastContainer, toast } from 'react-toastify';
import LoadingScreen from './LoadingScreen'; 

// FIX: Corrected import to use the specific image file Duelendscreen.png
// Assuming this path is correct relative to the component
import duelBackground from "../assets/Duels/Duelendscreen.png"; 


// --- STYLED COMPONENTS ---

// 1. Full background using the provided image
const DuelBackgroundContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 9;
  background-image: url(${duelBackground});
  background-size: cover;
  background-position: center;
`;

// 2. Application overlay/content container (now fully transparent)
const ApplicationContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 10;
  background: transparent; 
  padding: 100px 40px; /* Reduced top padding to center content better */
  font-size: 3em;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
`;

// Container for the scores (centered for both states)
const ScoreContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start; /* Align boxes to the top of the container */
  margin-top: 50px;
  width: 100%;
`;

const ResultBox = styled.div`
    margin: 0 20px;
    padding: 20px 20px; 
    border-radius: 10px;
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 250px; /* Increased width slightly for better content fit */
    background: #fff;
    box-shadow: 0 4px 8px rgba(0,0,0,0.2);
    height: 400px; /* Set a fixed height for consistency */
    justify-content: space-around;
    
    border: 4px solid ${props => 
        props.win === true ? '#42b231' : 
        props.win === false ? '#ef3e37' : 
        props.win === 'draw' ? '#2196f3' : '#e4e4e4'};
`;

// Placeholder box for the opponent in WAITING/ERROR state
const OpponentPlaceholderBox = styled(ResultBox)`
    min-width: 250px;
    justify-content: center;
    border: 4px solid #e4e4e4; 
    background: #f9f9f9;
`;

const ResultHeader = styled.h3`
    font-size: 1.5em;
    color: #683d8c;
    margin-bottom: 10px;
    white-space: nowrap;
`;

const ScoreLabel = styled.p`
    font-size: 1.2em;
    color: #333;
    margin: 5px 0;
    font-weight: 600;
`;

const ScoreValue = styled.p`
    font-size: 2.5em;
    font-weight: bold;
    color: #333;
    margin: 0;
`;

const WaitingMessage = styled.p`
    font-size: 0.8em; /* Adjusted font size */
    font-weight: 600;
    color: ${props => props.isError ? '#ef3e37' : '#2196f3'};
    margin: 10px 0 20px 0;
    text-align: center;
`;

const PlaceholderText = styled.p`
    font-size: 1.2em; 
    font-weight: bold;
    color: #666;
    text-align: center;
`;

const FeedbackWrapper = styled.div`
    background: #ffffff;
    border: 2px solid #673b8b;
    border-radius: 1em;
    padding: 20px;
    margin-top: 50px;
    color: #673c8c;
    font-size: 1em;
    width: 1200px;
    max-height: 400px;
    overflow-y: auto;
    
    h2 {
        text-align: center;
        margin-top: 0;
        color: #673c8c;
    }
`;

const ButtonGroup = styled.div`
    display: flex;
    justify-content: center;
    gap: 20px;
    margin-top: 30px;
    width: 100%;
`;


// --- MAIN COMPONENT ---

export default function GameResultDuel({
    duelData,
    onDuelEnd,
    pupil_id, 
    correct: playerCorrectCount, 
    incorrect, 
    onBack,
    feedback, 
    canExit,
}) {
  
  const [showFeedback, setShowFeedback] = useState(false);
  const retryCount = useRef(0); // Use ref to track retries without triggering re-render loop

  const [duelResult, setDuelResult] = useState({ 
    status: 'INITIAL', 
    message: 'Fetching duel status...', 
    opponentScore: null,
    playerCorrectCount: playerCorrectCount 
  });
  
  const { state: authState } = useAuth();
  
  const localPupilId = pupil_id; 
  const duelId = duelData?.id; 
  
  // --- DUEL SCORE FETCHING LOGIC ---
  const FetchDuelScores = async () => {
    
    if (['LOADING', 'COMPLETE', 'ERROR', 'WAITING'].includes(duelResult.status) && duelResult.status !== 'INITIAL') {
        return;
    }

    // Check auth/ID first
    if (!duelId || !localPupilId || !authState?.access_token) { 
        if (retryCount.current < 5) {
            retryCount.current += 1;
            setTimeout(FetchDuelScores, 1000); 
            return;
        }
        setDuelResult(prev => ({ 
            ...prev, 
            status: 'ERROR', 
            message: 'Error: Cannot retrieve duel results. Missing user/duel data.',
            playerCorrectCount: playerCorrectCount 
        }));
        return;
    }

    setDuelResult(prev => ({ ...prev, status: 'LOADING', message: 'Fetching duel status...' }));
    
    try {
        const response = await axios({
            method: 'get',
            url: `${process.env.REACT_APP_API}/duels/scores/${duelId}/${localPupilId}`,
            headers: {
                Authorization: `Bearer ${authState.access_token}`,
            },
            timeout: 10000 
        });

        const scoresResponse = response.data;
        
        if (!scoresResponse || !Array.isArray(scoresResponse.scores)) {
             setDuelResult({
                status: 'ERROR',
                message: "Score retrieval failed: Unexpected server response.",
                playerCorrectCount: playerCorrectCount,
                opponentScore: null,
             });
             return; 
        }

        const opponentScoreData = scoresResponse.scores.find(score => score.pupil_id !== localPupilId);
        const finalPlayerCorrect = scoresResponse.scores.find(score => score.pupil_id === localPupilId)?.correct || playerCorrectCount;

        if (opponentScoreData) {
            // STATUS: COMPLETE
            const opponentScore = opponentScoreData.correct;
            let playerResultText = '';
            let opponentResultText = '';
            
            if (finalPlayerCorrect > opponentScore) {
                playerResultText = "You Win!";
                opponentResultText = "Opponent Loses";
            } else if (finalPlayerCorrect < opponentScore) {
                playerResultText = "You Lose";
                opponentResultText = "Opponent Wins!";
            } else {
                playerResultText = "Draw";
                opponentResultText = "Draw";
            }

            setDuelResult({
                status: 'COMPLETE',
                playerResult: playerResultText,
                opponentResult: opponentResultText,
                opponentScore: opponentScore,
                playerCorrectCount: finalPlayerCorrect,
            });

        } else {
            // STATUS: WAITING
            setDuelResult({
                status: 'WAITING',
                message: "You now need to wait for your opponent to play.",
                playerCorrectCount: finalPlayerCorrect,
                opponentScore: null,
            });
        }
    } catch (error) {
        
        // Handle 403 or Timeout as WAITING
        if (axios.isCancel(error) || error.code === 'ECONNABORTED' || (error.response && error.response.status === 403)) {
            setDuelResult({ 
                status: 'WAITING', 
                message: "Waiting for opponent to play.",
                playerCorrectCount: playerCorrectCount
            });
        } 
        else {
            console.error("Failed to fetch duel scores:", error);
            setDuelResult({ 
                status: 'ERROR', 
                message: 'Error fetching duel scores. Check console for details.',
                playerCorrectCount: playerCorrectCount
            });
            toast.error("Error fetching duel scores.");
        }
    }
  };
    

  useEffect(() => {
    if (canExit && duelResult.status === 'INITIAL') {
        FetchDuelScores();
    }
  }, [canExit, duelResult.status, duelId, localPupilId, authState.access_token]); 

  // --- HANDLERS ---
  const handleExitClick = () => {
    // FIX 1: Always use the onBack prop for the final exit, as it contains the full cleanup logic
    // defined in DuelTopicSelection.js
    const finalAction = onBack || onDuelEnd || (() => console.warn("Exit function not defined!"));
    finalAction();
  };


  // --- RENDERING ---

  if (duelResult.status === 'INITIAL' || duelResult.status === 'LOADING') {
      return (
        // Show background during loading
        <DuelBackgroundContainer>
            <LoadingScreen message="Calculating Duel Results..." />
        </DuelBackgroundContainer>
      );
  }

  const buttonLabel = (duelResult.status === 'WAITING' || duelResult.status === 'ERROR') ? 'Exit' : 'Exit';
  const playerWinStatus = duelResult.playerResult === "You Win!" ? true : duelResult.playerResult === "Draw" ? 'draw' : false;
  const disabledExit = !canExit;


  return (
    <>
        <DuelBackgroundContainer />
              <Scale scaleTo={1720}>
        <ApplicationContainer>
            <ToastContainer />
                
                {/* FIX 2: Conditional rendering for Feedback/Scores in-line */}
                {showFeedback ? (
                    <FeedbackWrapper>
                         <h2>Feedback</h2>
                         <Feedback 
                            feedback={feedback} 
                            correct={duelResult.playerCorrectCount} 
                            incorrect={incorrect}
                         />
                    </FeedbackWrapper>
                ) : (
                    <>
                        <ScoreContainer>
                            
                            {/* Box 1: Player's Score (Left Box) */}
                            <ResultBox win={duelResult.status === 'COMPLETE' ? playerWinStatus : 'draw'}>
                                <ResultHeader>You</ResultHeader>
                                
                                <WaitingMessage isError={duelResult.status === 'ERROR'}>
                                    {duelResult.status !== 'COMPLETE' ? duelResult.message : duelResult.playerResult}
                                </WaitingMessage>
                                
                                <div style={{textAlign: 'center'}}>
                                    <ScoreLabel>Correct:</ScoreLabel>
                                    <ScoreValue>{duelResult.playerCorrectCount}</ScoreValue> 
                                </div>
                            </ResultBox>
                            
                            {/* Box 2: Opponent's Score/Placeholder (Right Box) */}
                            {duelResult.status === 'COMPLETE' ? (
                                // COMPLETE: Show actual opponent score
                                <ResultBox win={!playerWinStatus && playerWinStatus !== 'draw' ? true : playerWinStatus === 'draw' ? 'draw' : false}>
                                    <ResultHeader>Opponent</ResultHeader>
                                    <ScoreLabel>Correct:</ScoreLabel>
                                    <WaitingMessage isError={false}>
                                        {duelResult.opponentResult}
                                    </WaitingMessage>
                                    <ScoreValue>{duelResult.opponentScore}</ScoreValue>
                                </ResultBox>
                            ) : (
                                // WAITING/ERROR: Show placeholder box
                                <OpponentPlaceholderBox>
                                    <ResultHeader>Opponent</ResultHeader>
                                    <PlaceholderText>Waiting for opponent to finish.</PlaceholderText>
                                </OpponentPlaceholderBox>
                            )}
                        </ScoreContainer>
                    </>
                )}

                <ButtonGroup>
                    {/* Feedback Button - Only show if not viewing feedback AND if feedback exists */}
                    {feedback && feedback.length > 0 && !showFeedback && (
                        <GreenButton 
                            onClick={() => setShowFeedback(true)} 
                            disabled={disabledExit}
                            style={{ width: '400px', fontSize: '1.5em', height: '100px' }}
                        >
                            View Feedback
                        </GreenButton>
                    )}
                    {showFeedback && (
                         <RedButton 
                            onClick={() => setShowFeedback(false)} 
                            style={{ width: '400px', fontSize: '1.5em', height: '100px'}}
                         >
                            Hide Feedback
                         </RedButton>
                    )}
                    
                    {/* Final Exit Button */}
                    {/* Note: In the simplified logic, we always use onBack for cleanup/navigation */}
                    <Button 
                        onClick={handleExitClick}
                        disabled={disabledExit}
                        style={{ width: '400px', fontSize: '1.5em', height: '100px' }}
                    >
                        {buttonLabel}
                    </Button>
                </ButtonGroup>
        </ApplicationContainer>
        </Scale> 
    </>
  );
}